import React, { useState, useEffect } from "react";
import axios from "axios";
import Lottie from "lottie-react";
import "./../Styles/MESection.css";
import successAnimation from "./../ani/celebration.json";
import { useToast } from './ToastContext';
import { useConfirm } from './ConfirmDialog';
const apiUrl = process.env.REACT_APP_API_URL ;

/* ── Inline: Upload a New Project ────────────────────────── */
const UploadProjectInline = ({ onClose }) => {
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectName || !description) {
      setStatusMessage('Please fill in all required fields.');
      return;
    }
    const projectData = { project_name: projectName, description };
    try {
      const response = await fetch(`${apiUrl}/api/upload-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectData),
      });
      if (response.ok) {
        setProjectName(''); setDescription('');
        setStatusMessage('Project uploaded successfully!');
        setTimeout(() => { setStatusMessage(''); onClose(); }, 2000);
      } else {
        setStatusMessage('Failed to upload the project. Please try again.');
      }
    } catch (error) {
      setStatusMessage('An error occurred. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="pmodal-label">Project Name <span className="required-star">*</span></label>
        <input type="text" className="pmodal-input" placeholder="Enter project name" value={projectName} onChange={(e) => setProjectName(e.target.value)} required />
      </div>
      <div className="form-group">
        <label className="pmodal-label">Description <span className="required-star">*</span></label>
        <textarea className="pmodal-input pmodal-textarea" placeholder="Enter project description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} required />
      </div>
      {statusMessage && <p className="pmodal-helper" style={{ color: statusMessage.includes('success') ? 'green' : 'red' }}>{statusMessage}</p>}
      <div className="pmodal-footer">
        <button type="button" className="pmodal-btn-cancel" onClick={onClose}>Cancel</button>
        <button type="submit" className="pmodal-btn pmodal-btn-orange">Upload Project</button>
      </div>
    </form>
  );
};

/* ── Inline: Assign Project ──────────────────────────────── */
const AssignProjectInline = ({ onClose }) => {
  const [subManagers, setSubManagers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedLead, setSelectedLead] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    fetch(`${apiUrl}/api/sub-managers`).then(r => r.json()).then(setSubManagers).catch(() => {});
    fetch(`${apiUrl}/api/fetch-projects`).then(r => r.json()).then(setProjects).catch(() => {});
    fetch(`${apiUrl}/api/all-leads`).then(r => r.json()).then(data => setLeads(Array.isArray(data) ? data : [])).catch(() => {});
    fetch(`${apiUrl}/api/sub-manager-employees`).then(r => r.json()).then(setEmployees).catch(() => {});
  }, []);

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedProject || !selectedManager) {
      setStatusMessage('Please select both a project and a manager.');
      return;
    }
    try {
      const response = await fetch(`${apiUrl}/api/assign-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: selectedProject,
          manager_id: selectedManager,
          lead_id: selectedLead,
          employee_id: selectedEmployee
        }),
      });
      if (response.ok) {
        setStatusMessage('Project assigned successfully!');
        setSelectedManager(''); setSelectedProject(''); setSelectedLead(''); setSelectedEmployee('');
        setTimeout(() => { setStatusMessage(''); onClose(); }, 2000);
      } else {
        const err = await response.json();
        setStatusMessage(`Error: ${err.error}`);
      }
    } catch (error) {
      setStatusMessage('An error occurred. Please try again.');
    }
  };

  return (
    <form onSubmit={handleAssign}>
      <div className="form-group">
        <label className="pmodal-label">Project <span className="required-star">*</span></label>
        <select className="dropdown" value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} required>
          <option value="">Select Project</option>
          {projects.map((project) => (
            <option key={project._id} value={project._id}>{project.project_name}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label className="pmodal-label">Manager <span className="required-star">*</span></label>
        <select className="dropdown" value={selectedManager} onChange={(e) => setSelectedManager(e.target.value)} required>
          <option value="">Select Manager</option>
          {subManagers.map((manager) => (
            <option key={manager._id} value={manager.employee_number}>{manager.full_name}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label className="pmodal-label">Lead (Optional)</label>
        <select className="dropdown" value={selectedLead} onChange={(e) => setSelectedLead(e.target.value)}>
          <option value="">Select Lead</option>
          {leads
            .filter((lead) => String(lead.employee_number) !== String(selectedManager))
            .map((lead) => (
              <option key={lead._id || lead.employee_number} value={lead.employee_number}>{lead.full_name}</option>
            ))}
        </select>
      </div>
      <div className="form-group">
        <label className="pmodal-label">Employee (Optional)</label>
        <select className="dropdown" value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
          <option value="">Select Employee</option>
          {employees.map((emp) => (
            <option key={emp.employee_number || emp._id} value={emp.employee_number || emp._id}>{emp.full_name}</option>
          ))}
        </select>
      </div>
      {statusMessage && <p className="pmodal-helper" style={{ color: statusMessage.includes('success') ? 'green' : 'red' }}>{statusMessage}</p>}
      <div className="pmodal-footer">
        <button type="button" className="pmodal-btn-cancel" onClick={onClose}>Cancel</button>
        <button type="submit" className="pmodal-btn pmodal-btn-orange">Assign Project</button>
      </div>
    </form>
  );
};

/* ── Inline: Add Employee to Org ──────────────────────────── */
const AddEmployeeToOrgInline = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const showToast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !name || !employeeId) {
      setStatusMessage('Please fill in Email, Name, and Employee ID.');
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/add-to-org`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, employeeId }),
      });

      const data = await response.json();

      if (response.ok) {
        setEmail('');
        setName('');
        setEmployeeId('');
        showToast('Employee added to organization successfully!', 'success');
        setStatusMessage('Employee added to organization successfully!');
        setTimeout(() => { setStatusMessage(''); onClose(); }, 2000);
      } else {
        setStatusMessage(data.error || 'Failed to add employee to organization.');
      }
    } catch (error) {
      console.error('Error adding employee to org:', error);
      setStatusMessage('An error occurred. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="pmodal-label">Email <span className="required-star">*</span></label>
        <input
          type="email"
          className="pmodal-input"
          placeholder="Enter employee email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="form-group">
        <label className="pmodal-label">Name <span className="required-star">*</span></label>
        <input
          type="text"
          className="pmodal-input"
          placeholder="Enter employee name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div className="form-group">
        <label className="pmodal-label">Employee ID <span className="required-star">*</span></label>
        <input
          type="text"
          className="pmodal-input"
          placeholder="Enter employee ID"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          required
        />
      </div>
      {statusMessage && (
        <p className="pmodal-helper" style={{ color: statusMessage.includes('success') ? 'green' : 'red' }}>
          {statusMessage}
        </p>
      )}
      <div className="pmodal-footer">
        <button type="button" className="pmodal-btn-cancel" onClick={onClose}>Cancel</button>
        <button type="submit" className="pmodal-btn pmodal-btn-orange">Add employee</button>
      </div>
    </form>
  );
};

/* ── Inline: Delete Employee from Org ─────────────────────── */
const DeleteEmployeeFromOrgInline = ({ onClose }) => {
  const [employeesList, setEmployeesList] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const dropdownRef = React.useRef(null);
  const showToast = useToast();

  useEffect(() => {
    fetch(`${apiUrl}/api/all-org-employees`)
      .then((res) => res.json())
      .then((data) => {
        const arr = Array.isArray(data) ? data : [];
        setEmployeesList(arr.filter(emp => emp.role !== 'Main Manager'));
      })
      .catch((err) => {
        console.error('Error fetching employees list:', err);
      })
      .finally(() => {
        setLoading(false);
      });

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectEmployee = (emp) => {
    setSelectedEmpId(emp.employee_id);
    setSelectedEmployee(emp);
    setIsDropdownOpen(false);
    setStatusMessage('');
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!selectedEmployee || !selectedEmpId) {
      setStatusMessage('Please select an employee to delete.');
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/delete-from-org/${encodeURIComponent(selectedEmpId.trim())}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (response.ok) {
        setSelectedEmpId('');
        setSelectedEmployee(null);
        showToast('Employee deleted from organization successfully!', 'success');
        setStatusMessage('Employee deleted from organization successfully!');
        setTimeout(() => { setStatusMessage(''); onClose(); }, 2000);
      } else {
        setStatusMessage(data.error || 'Failed to delete employee from organization.');
      }
    } catch (error) {
      console.error('Error deleting employee from org:', error);
      setStatusMessage('An error occurred. Please try again.');
    }
  };

  return (
    <form onSubmit={handleDelete}>
      <div className="form-group" style={{ position: 'relative' }} ref={dropdownRef}>
        <label className="pmodal-label">Employee Name <span className="required-star">*</span></label>
        
        {/* Dropdown Input Header */}
        <div
          className="pmodal-input"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            backgroundColor: 'var(--bg-input)',
            color: selectedEmployee ? 'var(--text-primary)' : 'var(--text-secondary)',
            borderColor: isDropdownOpen ? '#3b82f6' : 'var(--border-strong)',
            boxShadow: isDropdownOpen ? '0 0 0 3px rgba(59, 130, 246, 0.15)' : 'none',
            borderRadius: '8px',
            padding: '10px 14px'
          }}
        >
          <span style={{ fontSize: '13.5px' }}>
            {selectedEmployee ? selectedEmployee.name : 'Select Employee Name'}
          </span>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', transition: 'transform 0.2s' }}>
            {isDropdownOpen ? '▲' : '▼'}
          </span>
        </div>

        {/* 1. Opens Downward, 2. Matches Input Width, 3. Max Height ~200px with Scroll, 4. No Clipping */}
        {isDropdownOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1000,
              marginTop: '4px',
              backgroundColor: 'var(--bg-elevated, #ffffff)',
              border: '1px solid var(--border, #e5e7eb)',
              borderRadius: '8px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)',
              maxHeight: '200px', // Max height ~200px
              overflowY: 'auto',   // Vertical scrollbar for items > limit
              boxSizing: 'border-box'
            }}
          >
            {loading ? (
              <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                Loading employees...
              </div>
            ) : employeesList.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                No employees found
              </div>
            ) : (
              employeesList.map((emp, index) => (
                <div
                  key={emp.employee_id || index}
                  onClick={() => handleSelectEmployee(emp)}
                  style={{
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontSize: '13.5px',
                    color: 'var(--text-primary)',
                    backgroundColor: selectedEmpId === emp.employee_id ? 'var(--bg-hover, #eff6ff)' : 'transparent',
                    borderBottom: index === employeesList.length - 1 ? 'none' : '1px solid var(--border, #f3f4f6)',
                    boxSizing: 'border-box',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-hover, #eff6ff)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedEmpId === emp.employee_id ? 'var(--bg-hover, #eff6ff)' : 'transparent'}
                >
                  {emp.name}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Selected Employee Details Card inside Dialogue */}
      {selectedEmployee && (
        <div style={{
          marginTop: '14px',
          padding: '14px 16px',
          borderRadius: '8px',
          backgroundColor: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          color: 'var(--text-primary)'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '10px', color: 'var(--text-primary)', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Employee Details</span>
            <span style={{
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '12px',
              backgroundColor: 'rgba(234, 88, 12, 0.15)',
              color: '#ea580c',
              fontWeight: '600'
            }}>
              {selectedEmployee.role || 'Member'}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: '13px' }}>
            <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Employee Name:</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{selectedEmployee.name}</span>

            <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Employee ID:</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{selectedEmployee.employee_id}</span>

            <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Email:</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{selectedEmployee.email || 'N/A'}</span>

            {selectedEmployee.department && (
              <>
                <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Department:</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{selectedEmployee.department}</span>
              </>
            )}
          </div>
        </div>
      )}

      {statusMessage && (
        <p className="pmodal-helper" style={{ color: statusMessage.includes('success') ? 'green' : 'red', marginTop: '10px' }}>
          {statusMessage}
        </p>
      )}

      <div className="pmodal-footer">
        <button type="button" className="pmodal-btn-cancel-white" onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="pmodal-btn pmodal-btn-orange"
          disabled={!selectedEmployee}
          style={{
            opacity: !selectedEmployee ? 0.6 : 1,
            cursor: !selectedEmployee ? 'not-allowed' : 'pointer'
          }}
        >
          Delete
        </button>
      </div>
    </form>
  );
};



const MESection = ({ managerName = "Sudheer" }) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [subManagers, setSubManagers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedSubManagerForAssignment, setSelectedSubManagerForAssignment] =
    useState(null); // For assignment in section 1
  const [selectedSubManagerForDetails, setSelectedSubManagerForDetails] =
    useState(null); // For viewing details in section 2
  const [selectedEmployees, setSelectedEmployees] = useState([]); // Track selected employees
  const [isEmployeeDropdownVisible, setEmployeeDropdownVisible] =
    useState(false); // Toggle dropdown visibility
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [dialogMessage, setDialogMessage] = useState("");
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // Track which modal is open

  useEffect(() => {
    // Fetch sub-managers and employees
    axios
      .get(`${apiUrl}/api/sub-managers`)
      .then((response) => {
        setSubManagers(response.data);
      })
      .catch((error) => {
        console.error("Error fetching sub-managers:", error);
      });

    axios
      .get(`${apiUrl}/api/sub-manager-employees`)
      .then((response) => {
        console.log("Employees fetched:", response.data); // Log the fetched employees
        setEmployees(response.data);
      })
      .catch((error) => {
        console.error("Error fetching employees:", error);
      });
  }, []);

  useEffect(() => {
    if (selectedSubManagerForDetails) {
      // Fetch assignments for the selected sub-manager in Section 2 (details)
      axios
        .get(`${apiUrl}/api/assigned-employees/${selectedSubManagerForDetails}`)
        .then((response) => {
          setAssignments(response.data);
          console.log("employeeunderamanager", response.data);
        })
        .catch((error) => {
          console.error("Error fetching assignments for sub-manager:", error);
        });
    }
  }, [selectedSubManagerForDetails]);

  const handleAssignEmployee = async () => {
    if (!selectedSubManagerForAssignment) {
      showToast("Please select a sub-manager.", "warning");
      return;
    }
    if (selectedEmployees.length === 0) {
      showToast("Please select at least one employee.", "warning");
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}/api/assign-employees`, {
        subManagerId: selectedSubManagerForAssignment,
        employeeIds: selectedEmployees,
      });
      showToast(response.data.message || "Employees assigned successfully!", "success");
      setSelectedEmployees([]); // Clear selected employees after assignment
    } catch (error) {
      console.error("Error assigning employees:", error);
      if (error.response && error.response.status === 409) {
          showToast(error.response.data.message || "Employees already assigned.", "warning");
      } else {
          showToast("Failed to assign employees. Please try again.", "error");
      }
    }
  };

  // Handle the checkbox change for employees
  const handleEmployeeCheckboxChange = (employeeNumber) => {
    setSelectedEmployees((prevSelected) => {
      if (prevSelected.includes(employeeNumber)) {
        return prevSelected.filter((emp) => emp !== employeeNumber); // Remove from selection
      } else {
        return [...prevSelected, employeeNumber]; // Add to selection
      }
    });
  };

  //conversion of employee to manager
  const handleConvertEmployee = async () => {
    if (!selectedEmployee) {
      showToast("Please select an employee to convert.", "warning");
      return;
    }

    const ok = await confirm({
      title: 'Promote to Manager',
      message: 'Are you sure you want to promote this employee to a Manager role? This action will change their system access.',
      confirmLabel: 'Promote',
      cancelLabel: 'Cancel',
      variant: 'info'
    });
    if (!ok) return;

    // Make the API call to convert the employee
    axios
      .post(`${apiUrl}/api/convert-to-sub-manager`, {
        employeeId: selectedEmployee,
      })
      .then((response) => {
        // Retrieve the employee name from the response
        const employeeName = response.data.name || selectedEmployee; // Fallback to ID if name is not available

        // Show dialog message
        setDialogMessage(
          `Employee ${employeeName} successfully converted to Sub-Manager (Admin).`,
        );
        setIsDialogVisible(true);

        // Remove the converted employee from the list
        setFilteredEmployees(
          filteredEmployees.filter(
            (emp) => emp.employee_number !== selectedEmployee,
          ),
        );
        setSelectedEmployee(null); // Reset selection
        setSearchTerm(""); // Reset search term

        // Hide dialog after 15 seconds
        setTimeout(() => {
          setIsDialogVisible(false);
          setDialogMessage(""); // Clear the message after hiding
        }, 15000);
      })
      .catch((error) => {
        console.error("Error converting employee:", error);
        showToast("Failed to convert employee. Please try again.", "error");
      });
  };

  // Handle employee search
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    setFilteredEmployees(
      employees.filter((employee) => {
        // Check if employee.full_name is not null or undefined
        if (employee.full_name) {
          return employee.full_name.toLowerCase().includes(value.toLowerCase());
        }
        return false; // Exclude employees without a full_name
      }),
    );
  };

  const closeModal = () => {
    setActiveModal(null);
    // Clear states when closing modal to refresh views
    setSelectedSubManagerForAssignment(null);
    setSelectedSubManagerForDetails(null);
    setSelectedEmployees([]);
    setEmployeeDropdownVisible(false);
    setSearchTerm("");
    setSelectedEmployee(null);
  };

  return (
    <div className="me-section-content">
      <div className="me-greeting-header">
        <h1 className="me-greeting-title">Hi {managerName}...</h1>
        <p className="me-greeting-subtitle">What would you like to do today?</p>
      </div>

      <div className="flex-container">
        
        {/* Dashboard Card 1 */}
        <div className="dashboard-card" onClick={() => setActiveModal('assign')}>
          <div className="card-top-row">
            <div className="card-icon-box purple-bg">👥</div>
            <h3 className="card-title-text">Assign Employees to Manager</h3>
          </div>
          <p className="card-desc-text">Assign employees to a specific manager.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box purple-border">→</div>
          </div>
        </div>

        {/* Dashboard Card 2 */}
        <div className="dashboard-card" onClick={() => setActiveModal('details')}>
          <div className="card-top-row">
            <div className="card-icon-box blue-bg">🛡️</div>
            <h3 className="card-title-text">Assigned Employee to Manager</h3>
          </div>
          <p className="card-desc-text">View employees already assigned to a manager.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box blue-border">→</div>
          </div>
        </div>

        {/* Dashboard Card 3 */}
        <div className="dashboard-card" onClick={() => setActiveModal('convert')}>
          <div className="card-top-row">
            <div className="card-icon-box green-bg">⇄</div>
            <h3 className="card-title-text">Convert Employee to Manager</h3>
          </div>
          <p className="card-desc-text">Convert an employee into a sub-manager.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box green-border">→</div>
          </div>
        </div>

        {/* Dashboard Card 4 */}
        <div className="dashboard-card" onClick={() => setActiveModal('upload')}>
          <div className="card-top-row">
            <div className="card-icon-box purple-bg">📁</div>
            <h3 className="card-title-text">Add a New Project</h3>
          </div>
          <p className="card-desc-text">Add and create a new project.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box purple-border">→</div>
          </div>
        </div>

        {/* Dashboard Card 5 */}
        <div className="dashboard-card" onClick={() => setActiveModal('assignProject')}>
          <div className="card-top-row">
            <div className="card-icon-box orange-bg">📝</div>
            <h3 className="card-title-text">Assign Project to Manager</h3>
          </div>
          <p className="card-desc-text">Assign a project to a specific manager.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box orange-border">→</div>
          </div>
        </div>

        {/* Dashboard Card 6 */}
        <div className="dashboard-card" onClick={() => setActiveModal('addOrg')}>
          <div className="card-top-row">
            <div className="card-icon-box blue-bg">🏢</div>
            <h3 className="card-title-text">Add Employee to Organization</h3>
          </div>
          <p className="card-desc-text">Add a new employee to the organization database.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box blue-border">→</div>
          </div>
        </div>

        {/* Dashboard Card 7 */}
        <div className="dashboard-card" onClick={() => setActiveModal('deleteOrg')}>
          <div className="card-top-row">
            <div className="card-icon-box red-bg">🗑️</div>
            <h3 className="card-title-text">Delete Employee from Organization</h3>
          </div>
          <p className="card-desc-text">Delete an employee from the organization database.</p>
          <div className="card-footer-row">
            <div className="card-arrow-box red-border">→</div>
          </div>
        </div>

      </div>

      {/* MODALS */}
      {activeModal && (
        <div className="me-modal-overlay" onClick={closeModal}>
          <div
            className={`me-modal-content ${(activeModal === 'deleteOrg' || activeModal === 'assign') ? 'overflow-visible' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="me-modal-close" onClick={closeModal}>✕</button>
            
            {activeModal === 'assign' && (
              <div className="employee-assignment-section">
                <div className="assign-modal-header">
                  <div className="assign-modal-header-icon">👥</div>
                  <div className="assign-modal-header-text">
                    <h2>Assign Employees to Manager</h2>
                    <p>Assign employees to a specific manager.</p>
                  </div>
                </div>
                <div className="assign-modal-divider" />
                <div className="assign-modal-body">
                  <div className="form-group">
                    <label className="assign-modal-label">Manager <span className="required-star">*</span></label>
                    <select
                      onChange={(e) => setSelectedSubManagerForAssignment(e.target.value)}
                      className="dropdown"
                      value={selectedSubManagerForAssignment || ""}
                    >
                      <option value="">Select Manager</option>
                      {subManagers.map((subManager) => (
                        <option key={subManager.employee_number} value={subManager.employee_number}>
                          {subManager.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label className="assign-modal-label">Employees <span className="required-star">*</span></label>
                    <div className="dropdown-header" onClick={() => setEmployeeDropdownVisible(!isEmployeeDropdownVisible)}>
                      {selectedEmployees.length === 0 ? "Select Employees" : `Selected Employees (${selectedEmployees.length})`}
                    </div>
                    {isEmployeeDropdownVisible && (
                      <div className="me-dropdown-container">
                        {employees.map((employee) => (
                          <div key={employee.employee_number} className="checkbox-item">
                            <input
                              type="checkbox"
                              id={`employee-${employee.employee_number}`}
                              checked={selectedEmployees.includes(employee.employee_number)}
                              onChange={() => handleEmployeeCheckboxChange(employee.employee_number)}
                            />
                            <label htmlFor={`employee-${employee.employee_number}`}>{employee.full_name}</label>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="assign-modal-helper">You can select multiple employees.</p>
                  </div>
                </div>
                <div className="assign-modal-footer">
                  <button className="assign-btn-cancel" onClick={closeModal}>Cancel</button>
                  <button className="assign-btn-primary" onClick={() => { handleAssignEmployee(); closeModal(); }}>Assign Employees</button>
                </div>
              </div>
            )}

            {activeModal === 'details' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon blue-icon">🛡️</div>
                  <div className="pmodal-header-text">
                    <h2>Assigned Employees to Manager</h2>
                    <p>View employees already assigned to a manager.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <div className="form-group">
                    <label className="pmodal-label">Manager <span className="required-star">*</span></label>
                    <select onChange={(e) => setSelectedSubManagerForDetails(e.target.value)} className="dropdown" value={selectedSubManagerForDetails || ""}>
                      <option value="">Select Manager</option>
                      {subManagers.map((subManager) => (
                        <option key={subManager.employee_number} value={subManager.employee_number}>
                          {subManager.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="pmodal-label">Assigned Employees</label>
                    <div className="assigned-employee-box">
                      {!selectedSubManagerForDetails ? (
                        <p className="pmodal-helper">Select a manager to view assigned employees.</p>
                      ) : assignments.length === 0 ? (
                        <p className="pmodal-helper">No employees assigned to this manager yet.</p>
                      ) : (
                        <ul className="assigned-employee-list">
                          {assignments.map((assignment) => (
                            <li key={assignment.employee_number} className="assigned-employee-item">
                              <span className="emp-name">{assignment.full_name}</span>
                              <span className="emp-id">{assignment.employee_number}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
                <div className="pmodal-footer">
                  <button className="pmodal-btn-cancel" onClick={closeModal}>Cancel</button>
                  <button className="pmodal-btn pmodal-btn-orange" onClick={closeModal}>Close</button>
                </div>
              </div>
            )}

            {activeModal === 'convert' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon green-icon">🔄</div>
                  <div className="pmodal-header-text">
                    <h2>Convert Employee to Manager</h2>
                    <p>Convert an employee into a manager.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <div className="form-group">
                    <label className="pmodal-label">Employee <span className="required-star">*</span></label>
                    <select
                      className="dropdown"
                      value={selectedEmployee || ""}
                      onChange={(e) => {
                        setSelectedEmployee(e.target.value);
                        setSearchTerm(e.target.value);
                        setFilteredEmployees(employees.filter(emp => emp.employee_number === e.target.value));
                      }}
                    >
                      <option value="">Select Employee</option>
                      {employees.map((emp) => (
                        <option key={emp.employee_number} value={emp.employee_number}>
                          {emp.full_name || 'Name not available'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="pmodal-footer">
                  <button className="pmodal-btn-cancel" onClick={closeModal}>Cancel</button>
                  <button className="pmodal-btn pmodal-btn-orange" onClick={() => { handleConvertEmployee(); closeModal(); }}>Convert to Manager</button>
                </div>
              </div>
            )}

            {activeModal === 'upload' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon purple-icon">📁</div>
                  <div className="pmodal-header-text">
                    <h2>Add a New Project</h2>
                    <p>Add and create a new project.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <UploadProjectInline onClose={closeModal} />
                </div>
              </div>
            )}

            {activeModal === 'assignProject' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon yellow-icon">📝</div>
                  <div className="pmodal-header-text">
                    <h2>Assign Project</h2>
                    <p>Assign a project to a manager and lead.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <AssignProjectInline onClose={closeModal} />
                </div>
              </div>
            )}

            {activeModal === 'addOrg' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon blue-icon">🏢</div>
                  <div className="pmodal-header-text">
                    <h2>Add Employee to Organization</h2>
                    <p>Add a new employee to the organization database.</p>
                  </div>

                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <AddEmployeeToOrgInline onClose={closeModal} />
                </div>
              </div>
            )}

            {activeModal === 'deleteOrg' && (
              <div className="pmodal-section">
                <div className="pmodal-header">
                  <div className="pmodal-header-icon red-icon">🗑️</div>
                  <div className="pmodal-header-text">
                    <h2>Delete Employee from Organization</h2>
                    <p>Delete an employee from the organization database.</p>
                  </div>
                </div>
                <div className="pmodal-divider" />
                <div className="pmodal-body">
                  <DeleteEmployeeFromOrgInline onClose={closeModal} />
                </div>
              </div>
            )}
            
          </div>
        </div>
      )}
      
      {/* Existing Lottie Success Dialog for Convert to Sub-Manager */}
      {isDialogVisible && (
        <div className="dialog-conversion">
          <Lottie animationData={successAnimation} loop={true} className="dialog-conversion-animation" />
          <p className="dialog-conversion-message">{dialogMessage}</p>
          <button className="conversion-close-button" onClick={() => setIsDialogVisible(false)}>Close</button>
        </div>
      )}
      
    </div>
  );
};

export default MESection;
